import { PermissionContext } from "../../../../../../../hooks/Remote/User/UserInfo/useRemoteFetchPermissionContext";
import { RemoteCateringMenu } from "../../../../../../ServiceManagement/Catering/Domain/Types/Remote-CateringMenu.types";
import { IEquipmentInventory } from "../../../../../Domain/Types/FloorPlan/Equipment.type";
import { RemoteRoomlayout } from "../../../../../Domain/Types/FloorPlan/RoomlayoutAssignment.type";
import { IFloorPayload } from "../../../../../Domain/Types/FloorPlan/FloorPayload.type";

interface Permission {
  companyIds: string[];
  costCenterUnitIds: string[];
  userIds: string[];
}

interface PermissionCheckProps {
  permissions: Permission | undefined;
  userId: string;
  costCenterId: string;
  companyId: number;
}

export const checkPermission = ({
  permissions,
  userId,
  costCenterId,
  companyId
}: PermissionCheckProps): boolean => {
  if (!permissions) return false;

  const { companyIds, costCenterUnitIds, userIds } = permissions;

  // Check if the provided userId, costCenterId, or companyId is in any of the arrays
  const isCompanyPermitted = companyIds.includes(companyId.toString()) || companyIds.length === 0;
  const isCostCenterPermitted =
    costCenterUnitIds.includes(costCenterId) || costCenterUnitIds.length === 0;
  const isUserPermitted = userIds.includes(userId) || userIds.length === 0;

  // Return true if any condition is true for the permission object
  return isUserPermitted && isCostCenterPermitted && isCompanyPermitted;
};

export function handleAssignedCateringItems({
  floorPlanForService,
  selectedZone,
  remoteCateringMenu,
  permissionContext,
  userId
}: {
  floorPlanForService: IFloorPayload;
  selectedZone: number;
  remoteCateringMenu: RemoteCateringMenu[];
  permissionContext: PermissionContext | undefined;
  userId: string;
}) {
  // assigned catering menu
  const assignedCatering = floorPlanForService?.cateringMenuAssignments?.find(
    zn => zn.zoneScheduleId === selectedZone
  );

  if (!assignedCatering || !permissionContext) return [];

  const assignedCateringItems = remoteCateringMenu
    .map(cat => {
      const menuItem = assignedCatering?.cateringMenuIds.find(men => cat.id === men.cateringMenuId);
      if (menuItem) return { ...cat, ...menuItem };
      return null;
    })
    .filter(item => item !== null)
    // check permissions
    .filter(item => {
      const isPermitted = checkPermission({
        permissions: item?.permissions || item?.servicePermissions,
        userId,
        companyId: permissionContext.companyId,
        costCenterId: permissionContext.costCenterUnitId ?? ""
      });

      if (isPermitted) return item;
      return null;
    });

  return assignedCateringItems;
}

export function handleAssignedBookableEquipItems({
  floorPlanForService,
  selectedZone,
  remoteBookableEquipment
}: {
  floorPlanForService: IFloorPayload;
  selectedZone: number;
  remoteBookableEquipment: IEquipmentInventory[];
}) {
  // assigned bookable equipment
  const assignedBookableEquip = floorPlanForService?.bookableEquipmentAssignments?.find(
    zn => zn.zoneScheduleId === selectedZone
  );

  if (!assignedBookableEquip) return [];

  const assignedBookableEquipItems = remoteBookableEquipment
    .map(equip => {
      const equipItem = assignedBookableEquip?.bookableEquipmentIds.find(
        eq => equip.id === eq.bookableEquipmentId
      );
      if (equipItem) return { ...equip, ...equipItem };
      return null;
    })
    .filter(item => item !== null);

  return assignedBookableEquipItems;
}

export function handleAssignedRoomLayItems({
  floorPlanForService,
  selectedZone,
  remoteRoomLayout
}: {
  floorPlanForService: IFloorPayload;
  selectedZone: number;
  remoteRoomLayout: RemoteRoomlayout[];
}) {
  // assigned room layout
  const assignedRoomLayout = floorPlanForService?.roomLayoutAssignments?.find(
    zn => zn.zoneScheduleId === selectedZone
  );

  if (!assignedRoomLayout) return [];

  const assignedRoomLayoutItems = remoteRoomLayout
    .map(layout => {
      const layoutItem = assignedRoomLayout?.roomLayoutIds.find(
        tab => layout.id === tab.roomLayoutId
      );
      if (layoutItem) return { ...layout, ...layoutItem };
      return null;
    })
    .filter(item => item !== null);

  return assignedRoomLayoutItems;
}
